import { Info } from "@phosphor-icons/react";
import clsx from "clsx";
import { useRef } from "react";
import { useTranslation } from "react-i18next";

import RenderIf from "common/components/RenderIf";
import Switch from "common/components/ui/Switch";
import { Tag } from "common/components/ui/Tag";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "common/components/ui/Tooltip";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import { useCampaignContext } from "../../context/CampaignContext";
import useCampaignMutations from "../../datahooks/useCampaignMutations";
import useValidateFlow from "../../hooks/useValidateFlow";
import CampaignFlow from "../CampaignFlow";
import FlowTypeLabel from "../FlowTypeLabel";

import type { CampaignFlow as CampaignFlowType } from "../../types";

function getScrollParent(node: HTMLElement) {
  if (node == null) {
    return null;
  }

  if (node.scrollHeight > node.clientHeight) {
    return node;
  } else {
    return getScrollParent(node.parentElement);
  }
}

interface InMailOptimizationProps {
  flow: CampaignFlowType;
  canToggle: boolean;
  isEnabled: boolean;
  targetCount: number;
}

export default function InMailOptimization({
  flow,
  canToggle,
  isEnabled,
  targetCount,
}: InMailOptimizationProps) {
  const isDesktop = useTwBreakpoint("lg");
  const { id: campaignId } = useCampaignContext();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { isFlowError, resetFlowError } = useValidateFlow(flow.id);

  const { updateCampaign } = useCampaignMutations();

  const { t } = useTranslation("campaigns", {
    keyPrefix: "flowTypes",
  });

  function updateOptimization(newValue: boolean) {
    if (newValue) {
      const scrollParent = getScrollParent(containerRef.current);
      setTimeout(() => {
        scrollParent.scrollTo(0, scrollParent.scrollHeight);
      }, 200);
    }
    updateCampaign({
      campaignId,
      updates: {
        inmail_optimization: newValue,
      },
    });
  }

  const isDisabled = !isEnabled && !targetCount;

  let flowWithTemplate = flow;
  if (!flow.template) {
    // We need to populate the flow template with initial data on the frontend if the template is null
    flowWithTemplate = {
      ...flow,
      template: {
        ai_descriptors: {},
        edited: false,
        message: "",
        original_id: null,
        subject: "",
        type: "INMAIL_TEMPLATE",
      },
    };
  }

  return (
    <article
      ref={containerRef}
      className={clsx(
        "rounded-3xl border p-3",
        isFlowError ? "border-red-500" : "border-black-200",
      )}
    >
      {/* Heading */}
      <section className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <FlowTypeLabel type={flow.type} isDisabled={isDisabled} />

          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-1">
              <h3
                className={clsx("text-lg-bold", isDisabled && "text-black-400")}
              >
                {t(`${flow.type}_Title`)}
              </h3>

              <Tag variant={isDisabled ? "disabled" : "secondary"}>
                {targetCount}
              </Tag>

              <RenderIf condition={isDesktop}>
                <Tooltip>
                  <TooltipTrigger>
                    <Info size={20} weight="fill" className="fill-black-300" />
                  </TooltipTrigger>
                  <TooltipContent>
                    {isDisabled
                      ? "InMails are disabled because there are no open profiles in this campaign"
                      : t(`${flow.type}_InfoText`)}
                  </TooltipContent>
                </Tooltip>
              </RenderIf>
            </div>

            <p
              className={clsx(
                "max-lg:hidden",
                isDisabled ? "text-black-400" : "text-black-500",
              )}
            >
              {t(`${flow.type}_Description`)}
            </p>
          </div>
        </div>

        <RenderIf condition={canToggle}>
          <Switch
            disabled={isDisabled}
            color="purple"
            checked={isEnabled}
            onCheckedChange={(checked) => {
              updateOptimization(checked);
              resetFlowError();
            }}
          />
        </RenderIf>
      </section>

      {/* Flow */}
      <RenderIf condition={isEnabled}>
        <div className="-mx-3 my-3 h-px bg-black-200 px-3" />
        <CampaignFlow
          flow={flowWithTemplate}
          isFlowError={isFlowError}
          resetFlowError={resetFlowError}
        />
      </RenderIf>
    </article>
  );
}
